import React, { useEffect, useMemo, useRef } from 'react';
import { LcaDatabasesListRecord } from 'types/dynamic-forms/records/LcaDatabasesListRecord';
import { JsonPointerPath } from 'types/dynamic-forms/JsonPointerPath';
import useProperty from '../../hooks/useProperty';
import usePropertyPath, { PropertyPathStackProvider } from '../../hooks/usePropertyPath';
import LcaDatabasesListRendererEntry from './LcaDatabasesListRendererEntry';
import { ExpandableHelpHtml } from 'components/v2/help-boxes/ExpandableHelpHtml';
import WizardAddButton from 'components/v2/epd-wizard-inputs/WizardAddButton';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import LcaDatabasesListRendererOther from './LcaDatabasesListRendererOther';
import { ErrorAlertPanel } from 'components/v2/compiler/panels/Styles.styled';

const DATABASES_SEGMENT = 'databases';

const LcaDatabasesListRenderer: React.FC<LcaDatabasesListRecord> = ({ propertyId }) => {
  const rootPath = usePropertyPath(propertyId);
  const { value: rootValue, setValue: setRootValue, isVisible, isDisabled } = useProperty<any>(rootPath);

  const databasesPath = useMemo(() => [...rootPath, DATABASES_SEGMENT] as JsonPointerPath, [rootPath]);
  const { value, setValue, errorMessage } = useProperty<any[]>(databasesPath);

  const migratedLegacyArrayRef = useRef(false);
  useEffect(() => {
    if (migratedLegacyArrayRef.current) {
      return;
    }
    if (Array.isArray(rootValue)) {
      migratedLegacyArrayRef.current = true;
      setRootValue({
        databases: rootValue,
        otherLcaDatabaseEnabled: 'No',
        otherLcaDatabaseName: '',
      });
    }
  }, [rootValue, setRootValue]);

  if (!isVisible) {
    return null;
  }

  const list = value ?? [];

  const confirmDeleteMaterialProperty = (index: number) => {
    confirmDelete({
      caption: 'Are you sure you want to delete database?',
      onConfirm: () => setValue(list.filter((_, i) => i !== index)),
    });
  };

  return (
    <>
      {errorMessage && <ErrorAlertPanel>{errorMessage}</ErrorAlertPanel>}

      {list.map((_, i) => (
        <PropertyPathStackProvider key={i} pushPath={[propertyId, DATABASES_SEGMENT, i]}>
          <LcaDatabasesListRendererEntry
            canBeDeleted={list.length >= 2}
            onDelete={() => confirmDeleteMaterialProperty(i)}
            onMakeMain={(isMain) =>
              setValue(
                list.map((item, index) => ({
                  ...item,
                  isMain: index === i ? isMain : 'No',
                }))
              )
            }
          />
        </PropertyPathStackProvider>
      ))}

      {!isDisabled && (
        <div>
          <div style={{ marginBottom: 10 }}>
            <ExpandableHelpHtml html='You can add more data sets if needed, or choose the "Other" option and provide the details.' />
          </div>
          <WizardAddButton label="+ Add data set" onClick={() => setValue([...list, {}])} />
        </div>
      )}

      <PropertyPathStackProvider pushPath={[propertyId]}>
        <LcaDatabasesListRendererOther />
      </PropertyPathStackProvider>
    </>
  );
};

export default LcaDatabasesListRenderer;
